import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import { getPasswordPoliciesApiCall } from '../../services/api/organizations';
import { changePasswordApiCall } from '../../services/api/users';
import { Organization } from '../../types/user';
import { evaluatePasswordPolicies } from '../../utils/helpers/passwordValidation';
import AlertMessage from '../common/AlertMessage/AlertMessage';
import PasswordRequirementsChecklist from '../common/PasswordRequirementsChecklist/PasswordRequirementsChecklist';
import EyeHiddenIcon from '../Icons/EyeHiddenIcon';
import EyeIcon from '../Icons/EyeIcon';

interface PasswordChangeModalProps {
  showPasswordModal: boolean;
  handleClosePasswordModal: () => void;
  showConfirmation: boolean;
  setShowConfirmation: (show: boolean) => void;
}

export function PasswordChangeModal({
  showPasswordModal,
  handleClosePasswordModal,
  showConfirmation,
  setShowConfirmation,
}: PasswordChangeModalProps) {
  const loggedInUser = useSelector((state: any) => state.user.user);
  const organizations = useSelector((state: any) => state.users.organizations) || [];

  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordAgain, setNewPasswordAgain] = useState<string>('');
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>(
    {
      show: false,
      type: 'success',
      message: '',
    }
  );

  const organizationId = loggedInUser?.OrganizationID;
  const passwordPolicy = useMemo(
    () => evaluatePasswordPolicies(newPassword, organization),
    [newPassword, organization]
  );
  const passwordsMismatch =
    newPasswordAgain.length > 0 && newPassword !== newPasswordAgain;
  const canSubmit =
    oldPassword.length > 0 &&
    newPassword.length > 0 &&
    newPasswordAgain.length > 0 &&
    passwordPolicy.isValid &&
    newPassword === newPasswordAgain;

  function clearForm() {
    setOldPassword('');
    setNewPassword('');
    setNewPasswordAgain('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }

  useEffect(() => {
    if (showPasswordModal) {
      clearForm();
    } else {
      setOrganization(null);
    }
  }, [showPasswordModal]);

  useEffect(() => {
    if (!showPasswordModal || !organizationId) {
      return;
    }

    const orgFromRedux = organizations.find(
      (org: Organization) => String(org.OrganizationID) === String(organizationId)
    );

    if (orgFromRedux) {
      setOrganization(orgFromRedux);
      return;
    }

    let isCancelled = false;

    getPasswordPoliciesApiCall(organizationId)
      .then(response => {
        if (!isCancelled && response.data) {
          setOrganization(response.data);
        }
      })
      .catch(error => {
        console.error('Error fetching password policies:', error);
        if (!isCancelled) {
          setOrganization(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [showPasswordModal, organizationId, organizations]);

  const handleClose = () => {
    handleClosePasswordModal();
    clearForm();
  };

  const handleDismissConfirmation = () => {
    setShowConfirmation(false);
    clearForm();
  };

  const handleSubmitPassword = () => {
    if (!canSubmit) {
      return;
    }
    setShowConfirmation(true);
    handleClosePasswordModal();
  };

  const handleConfirmPassword = () => {
    setLoading(true);
    changePasswordApiCall({
      oldPassword,
      newPassword,
    })
      .then(apiRes => {
        if (apiRes.data.success) {
          clearForm();
          setAlert({
            show: true,
            type: 'success',
            message: 'Password changed successfully',
          });
        } else {
          setAlert({
            show: true,
            type: 'error',
            message: 'Password change failed. Please contact the administrator.',
          });
        }
        setLoading(false);
        setShowConfirmation(false);
      })
      .catch(err => {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;

        if (status === 401) {
          setAlert({
            show: true,
            type: 'error',
            message: 'You have provided an incorrect password.',
          });
        } else {
          setAlert({
            show: true,
            type: 'error',
            message: message || 'Password change failed. Please try again.',
          });
        }
        setLoading(false);
        setShowConfirmation(false);
      });
  };

  return (
    <>
      <AlertMessage
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(prev => ({ ...prev, show: false }))}
      />
      <Dialog open={showPasswordModal} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              variant="filled"
              label="Old Password"
              type={showOldPassword ? 'text' : 'password'}
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <EyeHiddenIcon /> : <EyeIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant="filled"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="new-password"
              error={passwordsMismatch}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <EyeHiddenIcon /> : <EyeIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordRequirementsChecklist requirements={passwordPolicy.requirements} />
            <TextField
              variant="filled"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={newPasswordAgain}
              onChange={e => setNewPasswordAgain(e.target.value)}
              fullWidth
              size="small"
              autoComplete="new-password"
              error={passwordsMismatch}
              helperText={passwordsMismatch ? "Passwords don't match" : ''}
              FormHelperTextProps={{ sx: { minHeight: '1.25rem', mx: 0 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <EyeHiddenIcon /> : <EyeIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', py: 2, px: 3 }}>
          <Button onClick={handleClose} variant="outlined" size="small">
            Close
          </Button>
          <Button
            onClick={handleSubmitPassword}
            variant="contained"
            size="small"
            disabled={!canSubmit}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showConfirmation}
        onClose={handleDismissConfirmation}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Password Change</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to change your password?</Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
          <Button onClick={handleDismissConfirmation} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPassword}
            variant="contained"
            size="small"
            disabled={loading}
          >
            {loading ? <BeatLoader size={10} color="#fff" speedMultiplier={0.7} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
